/**
 * 扫码授权引导 —— 方案 §5.6 / 对接文档 §4.2-①②、§11 A-2
 *
 * 纯展示组件：**完全由 `useLpaasAuth` 的 phase 决定画面**，自己不存状态、不调接口
 * （依赖方向硬规矩：组件只 import constants/utils，不许 import hooks、services）。
 *
 * 四个画面的分工：
 *   needAuth   → 按 reason 出正文（首次 / 已过期 / 授权无效）+【向用户申请授权】
 *   authorizing→ 二维码（就是 authorizeUrl 本身）+ 等待文案 +【刷新二维码】
 *   timeout    → 120s 没扫上，给【去重新授权】
 *   ok         → 不渲染（由页面把引导关掉、直接开新增发布弹窗）
 *
 * 🔴 「没授权」不是「发布失败」：这里一个字都不许提失败（对接文档 §4.2-①处置矩阵）。
 * 🔴 关掉引导 = 什么都不发生：不开发布弹窗、不产生批次记录（页面侧 reset() 收尾）。
 */
/* eslint react/prop-types: "off" -- 本模块用 JSDoc 声明 props（见下方参数注释），不额外引 prop-types */
import React, { useEffect, useRef } from 'react';
import {
  Alert,
  Button,
  Collapse,
  Input,
  Modal,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd';
import QRCode from 'qrcode.react';
import { AUTH_PHASE, BTN, MANUAL_TOKEN, TEXT } from '../../constants';
import { isSafeHttpUrl } from '../../utils';
import styles from './authGuide.less';

const { Paragraph, Text } = Typography;

/** needAuth 的成因 → 正文（🔴 三种成因文案不同，别一律「请重新授权」） */
const REASON_TEXT = {
  firstTime: TEXT.AUTH_GUIDE_FIRST,
  expired: TEXT.AUTH_GUIDE_EXPIRED,
  invalid: TEXT.AUTH_GUIDE_INVALID,
};
const reasonText = (reason) => REASON_TEXT[reason] || TEXT.AUTH_GUIDE_FIRST;

export default function AuthGuideModal({
  visible,
  phase,
  authorizeUrl = '',
  qrText = '',
  reason = '',
  onStart,
  onCancel,
  /** 🔴 【临时方案】TEMPORARY-2026-10 手动token：入口开关（后端下发，生产恒 false） */
  manualTokenEntryEnabled = false,
  manualSubmitting = false,
  manualError = '',
  /** 凭证来源：SCAN / MANUAL（MANUAL 时标「临时授权」） */
  authSource = '',
  onSubmitManualToken,
}) {
  const needAuth = phase === AUTH_PHASE.NEED_AUTH;
  const authorizing = phase === AUTH_PHASE.AUTHORIZING;
  const timeout = phase === AUTH_PHASE.TIMEOUT;
  const hasQr = !!authorizeUrl;
  const manualMode = authSource === MANUAL_TOKEN.SOURCE_MANUAL;

  /**
   * 🔴 凭证值用「onChange 捕获到 ref」的方式拿，**不要去读输入框的 ref**：
   *    antd v4 的 {@code Input.Password} 的 ref <b>不是 DOM `<input>`</b> ——
   *    带 suffix（右侧眼睛图标）时 rc-input 的 ref 指向包装容器、且暴露的是 `{ input }` 形态，
   *    所以 `ref.current.value` 恒为 `undefined`。
   *    📌 2026-10-03 现场事故：用户粘了 token 点【保存并校验】“毫无反应”——
   *    就是这里读出了 `undefined` → 交出去一个空串 → 后端校验都没触发。
   *    已经用渲染体检 `authGuideModal.test.jsx` 钉住这条（逻辑单测照不出来）。
   *
   * 🔴 同时也<b>不进 React state</b>：这是完整登录态凭证（权限面大于“发布”），
   *    进 state 就会被 DevTools 快照 / 埋点包装类抓到。ref 只活在内存里，不触发重渲染。
   */
  const manualTokenRef = useRef('');

  // 🔴 关闭即丢弃：凭证只活在弹窗生命周期内。不清的话旧凭证留在 ref 里，
  //    下次打开引导直接点【保存并校验】会把上一轮的凭证静默重提（过期/换账号都不自知）
  useEffect(() => {
    if (!visible) manualTokenRef.current = '';
  }, [visible]);

  const handleManualSubmit = async () => {
    if (!onSubmitManualToken) return;
    // ⚠️ 空值也照常交上去：由 hook 统一给可见反馈（不许“点了没反应”）
    await onSubmitManualToken(manualTokenRef.current || '');
  };

  // 底部按钮：能点的只有「去申请/重新申请授权」和「取消」，没有任何「确认」类按钮 ——
  // 授权结果不由用户点按钮宣告，只由 phase 变化（扫码轮询）决定。
  const footer = [
    <Button key="cancel" onClick={onCancel}>
      {BTN.CANCEL}
    </Button>,
    needAuth || timeout ? (
      <Button key="go" type="primary" onClick={onStart}>
        {needAuth ? BTN.GO_AUTHORIZE : BTN.RE_AUTHORIZE}
      </Button>
    ) : (
      <Button key="retry" type="primary" ghost onClick={onStart}>
        {BTN.RE_AUTHORIZE}
      </Button>
    ),
  ];

  return (
    <Modal
      title={TEXT.AUTH_GUIDE_TITLE}
      open={visible}
      onCancel={onCancel}
      footer={footer}
      width={460}
      maskClosable={false}
      destroyOnClose
    >
      {needAuth && (
        <Alert
          type="info"
          showIcon
          message={
            <Space size={6}>
              <span>{reasonText(reason)}</span>
              {/* 🆕 临时方案：手贴凭证换来的授权要标出来，避免被当成正式扫码链路已打通 */}
              {manualMode ? (
                <Tag color="orange">{TEXT.AUTH_GUIDE_MANUAL_TAG}</Tag>
              ) : null}
            </Space>
          }
          description={
            <Text type="secondary">{TEXT.AUTH_GUIDE_CANCEL_TIP}</Text>
          }
        />
      )}

      {/*
        🔴 【临时方案】TEMPORARY-2026-10 手动token（节后整块删掉）：
        默认折叠，不抢扫码那条正式路径的注意力；显不显示**完全由后端开关决定**
        （Apollo manualTokenEnabled + 非生产 profile），前端不自己判断环境 ——
        前端自判等于把「生产不显示」交给一个改一行 JS 就能绕过的地方。
      */}
      {needAuth && manualTokenEntryEnabled && (
        <Collapse ghost className={styles.manualBox}>
          {/* ⚠️ antd v4：用 Collapse.Panel（v5 才支持 items 属性），不要顺手改成 items */}
          <Collapse.Panel
            key="manual"
            header={
              <Space size={6}>
                <Tag color="orange">{TEXT.AUTH_GUIDE_MANUAL_TAG}</Tag>
                <span>{TEXT.AUTH_GUIDE_MANUAL_TITLE}</span>
              </Space>
            }
          >
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              <Alert
                type="warning"
                showIcon
                message={TEXT.AUTH_GUIDE_MANUAL_TIP}
              />
              {/*
                Input.Password：避免肩窥。
                🔴 值靠 onChange 收进 ref（不用受控 state、也不读 input 的 ref，原因见上面注释）
              */}
              <Input.Password
                onChange={(e) => {
                  manualTokenRef.current = e.target.value;
                }}
                placeholder={TEXT.AUTH_GUIDE_MANUAL_PLACEHOLDER}
                autoComplete="off"
                disabled={manualSubmitting}
                onPressEnter={handleManualSubmit}
              />
              <Button
                type="primary"
                size="small"
                loading={manualSubmitting}
                onClick={handleManualSubmit}
              >
                {TEXT.AUTH_GUIDE_MANUAL_SUBMIT}
              </Button>
              {/* 失败只在这里就地提示（不改 phase、不当“还没扫”，拉后端原话过来） */}
              {manualError ? <Text type="danger">{manualError}</Text> : null}
              <Text type="secondary">{TEXT.AUTH_GUIDE_MANUAL_TOKEN_HINT}</Text>
            </Space>
          </Collapse.Panel>
        </Collapse>
      )}

      {authorizing && (
        <div className={styles.qrBox}>
          {hasQr ? (
            <>
              {/* 🔴 二维码内容 = 后端给的 authorizeUrl 本身，前端不许自己拼任何 URL */}
              <QRCode value={authorizeUrl} size={180} />
              <Paragraph type="secondary" className={styles.waiting}>
                <Spin size="small" /> {TEXT.AUTH_GUIDE_WAITING}
              </Paragraph>
            </>
          ) : (
            <Paragraph type="secondary">
              <Spin size="small" /> {TEXT.AUTH_GUIDE_NO_QR}
            </Paragraph>
          )}

          <Space direction="vertical" size={2} className={styles.links}>
            {/* 🔴 协议白名单：授权链接虽由后端下发，但渲染成 `<a href>` 前仍要过一遍 isSafeHttpUrl，
                不入白名单（javascript: 伪协议等）不渲染成链接，只保留二维码/可读串 */}
            {hasQr && isSafeHttpUrl(authorizeUrl) && (
              <a href={authorizeUrl} target="_blank" rel="noreferrer">
                {TEXT.AUTH_GUIDE_QR_HINT}
              </a>
            )}
            {/* qrText 是后端给的可读串（部分账号用飞书内部码），原样展示，不做解析 */}
            {qrText ? (
              <Text type="secondary" copyable={{ text: qrText }}>
                {qrText}
              </Text>
            ) : null}
          </Space>
        </div>
      )}

      {timeout && (
        <Alert
          type="warning"
          showIcon
          message={TEXT.AUTH_GUIDE_TIMEOUT}
          description={TEXT.AUTH_GUIDE_CANCEL_TIP}
        />
      )}
    </Modal>
  );
}
